/**
 * mpClient.ts —— 多人对战客户端：WebSocket 连接 + 可订阅状态 + 意图下发。
 *
 * 与单机 GameController 对称：所有网络时序都在 React 之外，UI 只用 useSyncExternalStore
 * 订阅快照并调用意图方法。模块级单例，避免 StrictMode 双挂载起两条连接。
 *
 * 红线：
 *   - 客户端永远不推进牌局，只发「动作意图」，等服务端回发权威视图；
 *   - 对局进行中不请求 / 不展示任何赢率数值（门控由 UI 保证，见 mp/ 组件）。
 */

import { createStore, type Store } from '../state/store';
import { pushToast } from '../state/toasts';
import type {
  ClientMsg, ServerMsg, RoomConfigInput, LobbyView, GameView, HandReview, MpErrorCode, Action,
} from './protocol';

export type MpConn = 'idle' | 'connecting' | 'open' | 'reconnecting' | 'closed';
export type MpScreen = 'setup' | 'lobby' | 'game';

export interface MpState {
  conn: MpConn;
  screen: MpScreen;
  code: string;
  youId: string;
  yourSeat: number;
  lobby: LobbyView | null;
  view: GameView | null;
  review: HandReview | null;
  reviewOpen: boolean;       // 复盘抽屉是否打开（对局中禁止，仅 handComplete 后可开）
  fatalError: string | null; // 阻断性错误（房间不存在/已满/进行中/连接失败）
  fatalCode: MpErrorCode | 'ws_failed' | 'ws_timeout' | null;
  /** 刷新/重连后正在恢复对局（UI 展示「正在同步对局状态…」毛玻璃遮罩）。 */
  resuming: boolean;
  /** 本场已结束手牌的复盘数据（供「手牌历史」查看；对局中不展示赢率）。 */
  history: readonly HandReview[];
  /** 首连/重连的二级进度（第 19 轮：唤醒服务 → 建立连接 → 同步房间信息）。 */
  connectStage: MpConnectStage;
}

/** 连接进度阶段（UI 展示二级反馈，纯展示用，不参与任何时序判定）。 */
export type MpConnectStage = 'idle' | 'waking' | 'handshaking' | 'syncing';

/** 各阶段的用户可读文案（顺序即步骤顺序）。 */
export const MP_CONNECT_STEPS: readonly { readonly stage: Exclude<MpConnectStage, 'idle'>; readonly label: string }[] = [
  { stage: 'waking', label: '唤醒房间服务' },
  { stage: 'handshaking', label: '建立连接' },
  { stage: 'syncing', label: '同步房间信息' },
];

export function mpConnectStageText(stage: MpConnectStage): string {
  switch (stage) {
    case 'waking': return '正在唤醒房间服务…';
    case 'handshaking': return '正在建立连接…';
    case 'syncing': return '正在同步房间信息…';
    default: return '正在连接房间服务…';
  }
}

const PID_KEY = 'riverside.mp.pid';
/** 多人会话持久化（刷新自动重连，BUG-R15-01 / AC4.1）。 */
const SESSION_KEY = 'riverside.mp.session';
/** 会话有效期：超过此时长不再尝试自动重连（房间 30 分钟自毁，取 10 分钟保守值）。 */
export const SESSION_TTL_MS = 10 * 60 * 1000;

export interface MpSession {
  readonly code: string;
  readonly name: string;
  /** 写入时的玩家标识，必须与当前 pid 一致才允许重连（避免串号）。 */
  readonly pid: string;
  readonly ts: number;
}

function loadPid(): string {
  try {
    let pid = localStorage.getItem(PID_KEY);
    if (!pid) { pid = crypto.randomUUID(); localStorage.setItem(PID_KEY, pid); }
    return pid;
  } catch { return Math.random().toString(36).slice(2); }
}

/** 会话读写：优先 sessionStorage（本标签页刷新），localStorage 兜底（部分浏览器恢复策略）。 */
function writeSession(s: MpSession): void {
  const raw = JSON.stringify(s);
  try { sessionStorage.setItem(SESSION_KEY, raw); } catch { /* noop */ }
  try { localStorage.setItem(SESSION_KEY, raw); } catch { /* noop */ }
}

export function readSession(): MpSession | null {
  const pick = (raw: string | null): MpSession | null => {
    if (!raw) return null;
    try {
      const s = JSON.parse(raw) as MpSession;
      if (!s || typeof s.code !== 'string' || !/^\d{6}$/.test(s.code)) return null;
      if (typeof s.pid !== 'string' || !s.pid) return null;
      if (typeof s.ts !== 'number' || Date.now() - s.ts > SESSION_TTL_MS) return null;
      return s;
    } catch { return null; }
  };
  let raw: string | null = null;
  try { raw = sessionStorage.getItem(SESSION_KEY); } catch { /* noop */ }
  const fromSession = pick(raw);
  if (fromSession) return fromSession;
  try { raw = localStorage.getItem(SESSION_KEY); } catch { return null; }
  return pick(raw);
}

export function clearSession(): void {
  try { sessionStorage.removeItem(SESSION_KEY); } catch { /* noop */ }
  try { localStorage.removeItem(SESSION_KEY); } catch { /* noop */ }
}

/**
 * 错误码 → 用户文案（BUG/D-S3：不再一律降级为「连接服务器失败」）。
 * 服务端已下发结构化错误码，客户端按码给出分类文案。
 */
export const MP_ERROR_TEXT: Readonly<Record<MpErrorCode | 'ws_failed' | 'ws_timeout', string>> = {
  bad_code: '房间号必须是 6 位数字',
  room_not_found: '房间号无效或已过期，请确认房主提供的房号',
  room_full: '该房间人数已满，请让房主新建房间或稍后再试',
  in_progress: '对局已在进行中，请等待下一场或联系房主',
  name_required: '请先填写昵称',
  not_host: '只有房主可以执行该操作',
  not_enough_players: '至少需要 2 名玩家才能开局',
  not_ready: '还有玩家未点击「准备」',
  seat_taken: '该座位已被占用，请选择其他座位',
  not_your_turn: '还没轮到你行动',
  illegal_action: '该动作不合法，请重新选择',
  internal: '服务器内部错误，请稍后重试',
  ws_failed: '连接服务器失败，请稍后重试',
  ws_timeout: '连接服务器超时，请检查网络后重试',
};

/** 取错误文案：优先服务端 message，缺失时回落到本地分类文案。 */
export function mpErrorText(code: MpErrorCode | 'ws_failed' | 'ws_timeout', serverMsg?: string): string {
  if (serverMsg && serverMsg.trim()) return serverMsg.trim();
  return MP_ERROR_TEXT[code] ?? MP_ERROR_TEXT.ws_failed;
}

/**
 * 生产环境房间服务地址的**源码内置兜底**（BUG-R18-01）。
 * 教训：构建期漏注入 `VITE_MP_WS_BASE` 时，旧实现会静默回退到「同源」，而站点是 SPA
 * （`/* → /index.html 200`），同源 `/api/ws` 会返回 200 HTML 使握手失败，用户只看到
 * 「连接服务器失败」。基址不再依赖构建期环境变量是否注入。
 */
export const DEFAULT_MP_WS_BASE = 'wss://riverside-mp.dbcwg001.workers.dev';

/** 同源 WebSocket 基址（生产站点由 Pages Functions `functions/api/[[path]].ts` 反代到 Worker）。 */
function sameOriginWsBase(origin?: string): string {
  if (origin) return origin.replace(/^http/, 'ws').replace(/\/$/, '');
  const loc = typeof location !== 'undefined' ? location : null;
  if (!loc || !loc.host) return '';
  return `${loc.protocol === 'https:' ? 'wss:' : 'ws:'}//${loc.host}`;
}

/** 候选线路计算所需的环境（显式传入便于单测，缺省时读运行时环境）。 */
export interface WsBaseEnv {
  /** 构建期注入的 VITE_MP_WS_BASE */
  injected?: string;
  /** 是否开发态 */
  dev?: boolean;
  /** 页面 origin（http/https），用于推导同源线路 */
  origin?: string;
}

function currentWsBaseEnv(): WsBaseEnv {
  const env = (import.meta as unknown as { env?: Record<string, unknown> }).env ?? {};
  return {
    injected: typeof env.VITE_MP_WS_BASE === 'string' ? env.VITE_MP_WS_BASE : '',
    dev: Boolean(env.DEV),
    origin: typeof location !== 'undefined' && location.host
      ? `${location.protocol}//${location.host}`
      : '',
  };
}

/**
 * 候选连接线路（按优先级排序，前一条握手失败会自动切到下一条，BUG-R18-01）：
 *   1. 构建期注入地址（有则最优先）；生产缺省时用内置默认 Worker 地址；
 *   2. 内置默认 Worker 地址（`*.workers.dev`）；
 *   3. 同源线路（Pages Functions 反代）—— 覆盖 `*.workers.dev` 被网络策略拦截的场景。
 * 开发态首选本地 wrangler（`ws://127.0.0.1:8787`），不连线上。
 */
export function wsBaseCandidates(env?: WsBaseEnv): string[] {
  const e = env ?? currentWsBaseEnv();
  const out: string[] = [];
  const push = (b: string): void => {
    const v = (b ?? '').replace(/\/$/, '');
    if (v && !out.includes(v)) out.push(v);
  };
  if (e.dev) push(e.injected || 'ws://127.0.0.1:8787');
  else { push(e.injected || ''); push(DEFAULT_MP_WS_BASE); }
  push(sameOriginWsBase(e.origin));
  return out;
}

/** 解析首选 WebSocket 基址（兼容旧调用点；完整候选见 wsBaseCandidates）。 */
export function resolveWsBase(): string {
  return wsBaseCandidates()[0] ?? '';
}

/**
 * Worker 预热（遗留项 L3 / L4）：Durable Object 冷启动会让首连等待 3–8s。
 * 进入多人入口时 fire-and-forget 打一次 /api/health，把 Worker 与 DO 命名空间提前唤醒。
 * 失败完全忽略（预热是纯优化，不能影响任何主流程）。
 */
let prewarmed = false;

export function resolveHttpBase(): string {
  return resolveWsBase().replace(/^ws/, 'http');
}

export function prewarmWorker(force = false): void {
  if (prewarmed && !force) return;
  prewarmed = true;
  const base = resolveHttpBase();
  if (!base || typeof fetch !== 'function') return;
  try {
    void fetch(`${base}/api/health`, { method: 'GET', mode: 'cors', cache: 'no-store' }).catch(() => { /* noop */ });
  } catch { /* noop */ }
  // 顺便把同源兜底线路探清楚（OBS-R18-01）：用户真正点连接时结论已就绪，无需现场等待
  for (const b of wsBaseCandidates()) if (wsBaseNeedsProbe(b)) probeWsBaseInBackground(b);
}

/* ---------------- 同源线路探测（OBS-R18-01 控制台降噪） ---------------- */

/**
 * 同源兜底线路（`/api/ws`）**只有在站点确实挂了 Pages Functions 反代时才可用**。
 * 若站点是纯 SPA（`/* → /index.html 200`），直接开 WebSocket 会让浏览器打印一条无法拦截的
 * 红色 `WebSocket connection ... failed / handshake timeout`，即使前面的线路已经连上也照样刷屏。
 *
 * 因此改为：**先用 HTTP 探一次 `/api/health`**（fetch 失败 / 返回 HTML 都不会产生红色 WS 报错），
 * 只有确认是真反代（2xx + 非 HTML）才真正发起同源 WebSocket，否则安静跳过这条线路。
 * f81617c 的同源兜底能力与旧缓存自愈提示均保留：反代存在时行为完全不变。
 */
const probeCache = new Map<string, boolean>();
const probeInFlight = new Map<string, Promise<boolean>>();

export function resetWsProbeCache(): void { probeCache.clear(); probeInFlight.clear(); }

/** 探测结论（true/false/未知），供**同步**的线路决策使用。 */
export function wsProbeResult(base: string): boolean | undefined { return probeCache.get(base); }

/** 手动写入探测结论（测试与本地调试用）。 */
export function primeWsProbe(base: string, ok: boolean): void { probeCache.set(base, ok); }

/** 后台探测（不阻塞任何连接决策）。 */
export function probeWsBaseInBackground(base: string): void {
  if (probeCache.has(base) || probeInFlight.has(base)) return;
  const p = probeWsBase(base);
  probeInFlight.set(base, p);
  void p.finally(() => probeInFlight.delete(base));
}

export async function probeWsBase(base: string): Promise<boolean> {
  const cached = probeCache.get(base);
  if (cached !== undefined) return cached;
  let ok = false;
  if (typeof fetch === 'function') {
    try {
      const res = await fetch(`${base.replace(/^ws/, 'http')}/api/health`, { method: 'GET', cache: 'no-store' });
      const ct = res.headers?.get?.('content-type') ?? '';
      ok = !!res.ok && !/text\/html/i.test(ct);
    } catch { ok = false; }
  }
  probeCache.set(base, ok);
  return ok;
}

/** 该线路是否需要先探测（仅同源兜底线路需要，直连 Worker 线路不加这一跳延迟）。 */
export function wsBaseNeedsProbe(base: string, env?: WsBaseEnv): boolean {
  const same = sameOriginWsBase(env?.origin);
  return !!same && base === same;
}

const INITIAL: MpState = {
  conn: 'idle', screen: 'setup', code: '', youId: '', yourSeat: -1,
  lobby: null, view: null, review: null, reviewOpen: false, fatalError: null, fatalCode: null,
  resuming: false, history: [], connectStage: 'idle',
};

/** 建连超时（ms）：超过则给出「连接超时」而非笼统失败文案。 */
const CONNECT_TIMEOUT_MS = 15000;

/** 重连预算（ms）：对齐 PRD §6「断线 30 秒内重连可恢复对局」。 */
export const RECONNECT_BUDGET_MS = 30000;
/** 心跳间隔与静默超时（BUG-R16-01）：服务端已支持 ping→pong（worker/room.ts）。 */
export const HEARTBEAT_INTERVAL_MS = 5000;
export const HEARTBEAT_TIMEOUT_MS = 12000;
/**
 * 重连后「首包同步」超时（ms，BUG-R17-01）：
 * 半开连接（握手成功但消息不通）下 socket 会正常 open，若在此窗口内仍未收到任何权威视图
 * （lobby / state），判定为假恢复 → 主动丢弃连接并继续重连，期间同步遮罩保持不撤销。
 */
export const FIRST_SYNC_TIMEOUT_MS = 5000;

const FATAL_CONNECT_CODES: ReadonlySet<MpErrorCode> = new Set([
  'bad_code', 'room_not_found', 'room_full', 'in_progress', 'name_required',
]);

/**
 * 「业务性终态」：确定性失败，重试无意义 → 清理持久化会话，避免误重连（BUG-R16-02）。
 * 网络类失败（ws_failed / ws_timeout）**不在此列**，会话保留至 TTL 到期，网络恢复后仍可自动重入。
 */
const SESSION_CLEAR_CODES: ReadonlySet<MpErrorCode | 'ws_failed' | 'ws_timeout'> = FATAL_CONNECT_CODES;

class MpClient {
  readonly store: Store<MpState> = createStore<MpState>(INITIAL);
  private ws: WebSocket | null = null;
  private pid = loadPid();
  private lastConnect: { mode: 'create' | 'join'; name: string; code: string; config?: RoomConfigInput } | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectAttempts = 0;
  private manualClose = false;
  private connectTimer: ReturnType<typeof setTimeout> | null = null;
  /** 本次连接是否已收到服务端任一业务消息（joined/error…），用于错误分类。 */
  private gotServerMsg = false;
  /** 本轮重连的截止时间戳（首次断线时设定，= now + RECONNECT_BUDGET_MS）。 */
  private reconnectDeadline = 0;
  /** 心跳定时器与最近一次收到服务端消息的时间戳（静默掉线看门狗）。 */
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private lastServerMsgAt = 0;
  /** 本次连接是否处于「已连接、待首包同步」中间态（重连/恢复路径专用）。 */
  private awaitingFirstSync = false;
  private firstSyncTimer: ReturnType<typeof setTimeout> | null = null;
  /** 当前使用的候选线路下标（BUG-R18-01：首选线路握手失败时自动切到下一条）。 */
  private baseIndex = 0;
  /** 本轮已尝试过的线路（用于失败诊断文案）。 */
  private triedBases: string[] = [];
  /** 本次连接的 socket 是否完成过握手（open 事件）——区分「握手失败」与「握手成功后被服务端关闭」。 */
  private socketOpened = false;
  /** 异步探测的时序令牌：探测返回时若已发起新的 open，则丢弃旧结果。 */
  private openToken = 0;

  private patch(p: Partial<MpState>): void { this.store.set((prev) => ({ ...prev, ...p })); }
  get state(): MpState { return this.store.get(); }

  /* ---------------- 连接 ---------------- */

  createRoom(name: string, config: RoomConfigInput): void {
    this.lastConnect = { mode: 'create', name, code: '', config };
    this.resetBaseFailover();
    this.open();
  }

  joinRoom(code: string, name: string): void {
    this.lastConnect = { mode: 'join', name, code };
    this.resetBaseFailover();
    this.open();
  }

  /** 是否存在可自动恢复的多人会话（刷新后由 App 调用，AC4.1）。 */
  hasResumableSession(): boolean {
    const s = readSession();
    return !!s && s.pid === this.pid;
  }

  /**
   * 刷新后自动重入房间：以「同 pid + 同房号」join，服务端按 pid 认作重连，
   * 恢复座位 / 筹码 / 底牌。恢复期间 resuming=true，由 UI 展示同步遮罩。
   */
  resume(): boolean {
    const s = readSession();
    if (!s || s.pid !== this.pid) { clearSession(); return false; }
    this.patch({ resuming: true, code: s.code });
    this.lastConnect = { mode: 'join', name: s.name, code: s.code };
    this.resetBaseFailover();
    this.open();
    return true;
  }

  /* ---------------- 线路故障切换（BUG-R18-01） ---------------- */

  /** 新的一次用户主动连接：从首选线路重新开始尝试。 */
  private resetBaseFailover(): void {
    this.baseIndex = 0;
    this.triedBases = [];
  }

  /**
   * 当前连接在「一条服务端消息都没收到」的情况下失败时，切换到下一条候选线路重试。
   * 典型场景：`*.workers.dev` 被网络策略拦截 → 自动改走同源反代通道。
   * 返回 true 表示已发起下一条线路的连接（调用方不应再报错/走重连预算）。
   */
  private tryNextBase(): boolean {
    if (this.manualClose || this.gotServerMsg) return false;
    const bases = wsBaseCandidates();
    if (this.baseIndex + 1 >= bases.length) return false;
    this.baseIndex++;
    this.cleanupSocket();
    this.open();
    return true;
  }

  /** 已尝试过的线路（失败诊断用，UI 可展示）。 */
  attemptedBases(): readonly string[] { return this.triedBases; }

  private persistSession(code: string): void {
    if (!/^\d{6}$/.test(code)) return;
    writeSession({ code, name: this.lastConnect?.name ?? '', pid: this.pid, ts: Date.now() });
  }

  private open(): void {
    if (!this.lastConnect) return;
    this.manualClose = false;
    this.cleanupSocket();
    // 重连过程中保持 reconnecting，避免同步遮罩在「连接中」瞬间闪断
    const reconnecting = this.state.conn === 'reconnecting';
    this.patch({
      conn: reconnecting ? 'reconnecting' : 'connecting',
      fatalError: null, fatalCode: null,
      // 二级进度（第 19 轮）：首连先展示「唤醒房间服务」，握手发起后切「建立连接」
      connectStage: this.state.connectStage === 'idle' ? 'waking' : this.state.connectStage,
    });

    const bases = wsBaseCandidates();
    const base = bases[Math.min(this.baseIndex, Math.max(0, bases.length - 1))] ?? '';
    if (!base) { this.fail('ws_failed', '未配置多人对战服务地址'); return; }
    if (!this.triedBases.includes(base)) this.triedBases.push(base);

    /*
     * OBS-R18-01：同源兜底线路先做 HTTP 探测再开 WS，避免 SPA fallback 场景下浏览器
     * 打印无法拦截的红色 handshake 报错（探测失败则安静跳到下一条 / 结束）。
     */
    if (wsBaseNeedsProbe(base) && wsProbeResult(base) !== true) {
      // 结论未知则后台探一次（下次尝试即可直接命中缓存），本次安静跳过，绝不发起注定失败的 WS
      probeWsBaseInBackground(base);
      if (this.tryNextBase()) return;
      this.fail('ws_failed', mpErrorText('ws_failed'));
      return;
    }
    this.openSocket(base, reconnecting);
  }

  /** 真正发起 WebSocket 连接（base 已确定可尝试）。 */
  private openSocket(base: string, reconnecting: boolean): void {
    if (!this.lastConnect) return;
    this.openToken++;
    this.patch({ connectStage: 'handshaking' });
    const { mode, name, code, config } = this.lastConnect;
    const q = new URLSearchParams();
    q.set('mode', mode);
    q.set('name', name);
    q.set('pid', this.pid);
    if (mode === 'join') q.set('code', code);
    if (this.state.code) q.set('code', this.state.code); // 重连时带上已知房间号
    if (mode === 'create' && config) {
      q.set('maxSeats', String(config.maxSeats));
      q.set('sb', String(config.smallBlind));
      q.set('bb', String(config.bigBlind));
      q.set('stack', String(config.startingStack));
      q.set('timeout', String(config.actionTimeoutSec));
    }

    this.gotServerMsg = false;
    this.socketOpened = false;
    let ws: WebSocket;
    try { ws = new WebSocket(`${base}/api/ws?${q.toString()}`); }
    catch {
      // 构造即失败（地址非法 / 协议被禁）：先尝试下一条线路，全部用尽再报错
      if (this.tryNextBase()) return;
      this.fail('ws_failed', mpErrorText('ws_failed'));
      return;
    }
    this.ws = ws;

    // 建连超时兜底：避免长时间停留在「连接中…」且最终只报笼统失败
    this.connectTimer = setTimeout(() => {
      if (this.state.conn === 'connecting' || this.state.conn === 'reconnecting') {
        this.cleanupSocket();
        if (this.tryNextBase()) return;
        this.fail('ws_timeout', mpErrorText('ws_timeout'));
      }
    }, CONNECT_TIMEOUT_MS);

    ws.addEventListener('open', () => {
      this.socketOpened = true;
      if (this.connectTimer) { clearTimeout(this.connectTimer); this.connectTimer = null; }
      /*
       * BUG-R17-01：重连/恢复路径下，socket.open **不等于**恢复成功。
       * 只有收到服务端首个权威视图（lobby / state）才算真恢复；在此之前保持 reconnecting，
       * 同步遮罩不撤销，并用 FIRST_SYNC_TIMEOUT_MS 兜底识别半开连接。
       */
      this.patch({ connectStage: 'syncing' });
      const isRecovery = reconnecting || this.state.resuming;
      if (isRecovery) {
        this.awaitingFirstSync = true;
        this.armFirstSyncTimer();
      } else {
        this.reconnectAttempts = 0;
        this.reconnectDeadline = 0;
        this.patch({ conn: 'open' });
      }
      this.startHeartbeat();
    });
    ws.addEventListener('message', (evt) => {
      let msg: ServerMsg;
      try { msg = JSON.parse(typeof evt.data === 'string' ? evt.data : ''); } catch { return; }
      this.gotServerMsg = true;
      this.lastServerMsgAt = Date.now();
      // 收到权威视图 = 重连真正成功（撤销遮罩、重置退避预算）
      if (this.awaitingFirstSync && (msg.t === 'lobby' || msg.t === 'state')) {
        this.awaitingFirstSync = false;
        this.clearFirstSyncTimer();
        this.reconnectAttempts = 0;
        this.reconnectDeadline = 0;
        this.patch({ conn: 'open' });
      }
      if (msg.t === 'lobby' || msg.t === 'state') this.patch({ connectStage: 'idle' });
      this.onMessage(msg);
    });
    /*
     * 只处理「当前连接」的 close：我们主动 cleanupSocket() 关闭的旧 socket 不应再触发
     * 线路切换 / 重连（否则会与调用方的后续处理重入，把新建的连接又清掉）。
     */
    ws.addEventListener('close', () => { if (this.ws === ws) this.onSocketClose(); });
    ws.addEventListener('error', () => { /* close 会随后触发 */ });
  }

  /* ---------------- 首包同步看门狗（BUG-R17-01） ---------------- */

  private armFirstSyncTimer(): void {
    this.clearFirstSyncTimer();
    this.firstSyncTimer = setTimeout(() => {
      if (!this.awaitingFirstSync) return;
      // 已连接但拿不到任何权威视图 → 半开连接，丢弃重连（遮罩持续显示）
      this.onConnectionDead();
    }, FIRST_SYNC_TIMEOUT_MS);
  }

  private clearFirstSyncTimer(): void {
    if (this.firstSyncTimer) { clearTimeout(this.firstSyncTimer); this.firstSyncTimer = null; }
  }

  /* ---------------- 心跳看门狗（BUG-R16-01） ---------------- */

  /**
   * 定时 ping + 静默超时判定：网络「冻结」时 socket 不会触发 close 事件，
   * 若 HEARTBEAT_TIMEOUT_MS 内没有收到任何服务端消息（含 pong），主动判定断线并走既有重连流程。
   * 服务端 ping→pong 能力已存在，无需改 worker/。
   */
  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.lastServerMsgAt = Date.now();
    this.heartbeatTimer = setInterval(() => {
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
      if (Date.now() - this.lastServerMsgAt > HEARTBEAT_TIMEOUT_MS) { this.onConnectionDead(); return; }
      this.send({ t: 'ping' });
    }, HEARTBEAT_INTERVAL_MS);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer) { clearInterval(this.heartbeatTimer); this.heartbeatTimer = null; }
  }

  /** 静默掉线：丢掉僵死连接并立即进入重连态（展示同步遮罩）。 */
  private onConnectionDead(): void {
    if (this.manualClose) return;
    this.cleanupSocket();
    if (!this.scheduleReconnect()) this.fail('ws_failed', mpErrorText('ws_failed'));
  }

  /** 在 30s 预算内安排下一次重连；预算耗尽返回 false。 */
  private scheduleReconnect(): boolean {
    const s = this.state;
    if (s.screen !== 'lobby' && s.screen !== 'game' && !s.resuming) return false;
    if (this.reconnectDeadline === 0) this.reconnectDeadline = Date.now() + RECONNECT_BUDGET_MS;
    if (Date.now() >= this.reconnectDeadline) return false;
    this.reconnectAttempts++;
    this.patch({ conn: 'reconnecting' });
    const delay = Math.min(1000 * this.reconnectAttempts, 5000);
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = setTimeout(() => {
      // 重连一律用 join + 已知房间号（服务端按 pid 认作重连回原座位）
      this.lastConnect = { mode: 'join', name: this.lastConnect?.name ?? '', code: this.state.code };
      this.open();
    }, delay);
    return true;
  }

  private onSocketClose(): void {
    this.stopHeartbeat();
    if (this.manualClose) { this.patch({ conn: 'closed' }); return; }
    /*
     * BUG-R18-01：握手阶段就失败（socket 从未 open，例如线路被拦截、或同源 /api/ws 被 SPA
     * fallback 返回 200 HTML）→ 立刻切换到下一条候选线路重试，不消耗重连预算、不报错。
     */
    if (!this.socketOpened && !this.gotServerMsg && this.tryNextBase()) return;
    const s = this.state;
    // 服务端已回发过分类错误（房间不存在/满座/已开局…）→ 不要用通用文案覆盖
    if (s.fatalCode && s.fatalCode !== 'ws_failed') { this.patch({ conn: 'closed' }); return; }
    // 已进入大厅/对局，或正在刷新恢复中，才尝试重连（30s 预算内，对齐 PRD）
    if (this.scheduleReconnect()) return;
    // 已握手但服务端一句业务消息都没回就断开 → 加入的房间大概率不存在/已过期，别报笼统连接失败
    if (s.conn === 'open' && !this.gotServerMsg && this.lastConnect?.mode === 'join') {
      this.fail('room_not_found', mpErrorText('room_not_found'));
      return;
    }
    if (s.conn === 'connecting' || s.conn === 'open' || s.resuming) this.fail('ws_failed', mpErrorText('ws_failed'));
    else this.patch({ conn: 'closed' });
  }

  private fail(code: MpErrorCode | 'ws_failed' | 'ws_timeout', message: string): void {
    const wasResuming = this.state.resuming;
    this.stopHeartbeat();
    this.awaitingFirstSync = false;
    this.clearFirstSyncTimer();
    // 仅业务性终态（房间不存在/已过期/满座/已开局/非法房号）清理会话；
    // 网络类失败（ws_failed/ws_timeout）保留会话，网络恢复后仍可自动重入（BUG-R16-02）。
    const clearable = SESSION_CLEAR_CODES.has(code);
    if (clearable) clearSession();
    this.patch({ conn: 'closed', screen: 'setup', fatalError: message, fatalCode: code, resuming: false, connectStage: 'idle' });
    if (wasResuming) {
      pushToast(
        clearable ? `未能回到原房间：${message}` : `${message}（房间信息已保留，网络恢复后可点「回到房间」重试）`,
        'warning',
      );
    }
  }

  /* ---------------- 收消息 ---------------- */

  private onMessage(msg: ServerMsg): void {
    switch (msg.t) {
      case 'joined':
        this.patch({ code: msg.code, youId: msg.youId, yourSeat: msg.seat });
        this.persistSession(msg.code); // 刷新自动重连所需（AC4.1）
        return;
      case 'error':
        if (FATAL_CONNECT_CODES.has(msg.code as MpErrorCode)) {
          this.fail(msg.code as MpErrorCode, mpErrorText(msg.code as MpErrorCode, msg.message));
          this.cleanupSocket();
        } else pushToast(mpErrorText(msg.code as MpErrorCode, msg.message), 'warning');
        return;
      case 'lobby':
        this.patch({
          lobby: msg.room, code: msg.room.code, yourSeat: msg.room.yourSeat, screen: 'lobby',
          review: null, reviewOpen: false, resuming: false,
        });
        if (msg.room.phase === 'ended') clearSession();
        else this.persistSession(msg.room.code);
        return;
      case 'state': {
        const wasReview = this.state.reviewOpen;
        const nowComplete = msg.view.phase === 'handComplete' || msg.view.phase === 'gameOver';
        this.patch({ view: msg.view, screen: 'game', reviewOpen: nowComplete ? wasReview : false, resuming: false });
        if (msg.view.phase === 'gameOver') clearSession(); // 本场结束不再自动重连
        else this.persistSession(this.state.code);
        return;
      }
      case 'handEnd': {
        const prev = this.state.history.filter((h) => h.handNumber !== msg.review.handNumber);
        this.patch({ review: msg.review, history: [...prev, msg.review] });
        return;
      }
      case 'gameOver':
        clearSession();
        pushToast(`🏆 ${msg.winnerName} 赢得了本场对局`, 'success');
        return;
      case 'toast':
        pushToast(msg.message, msg.level);
        return;
      case 'pong':
        return;
      default:
        return;
    }
  }

  /* ---------------- 发意图 ---------------- */

  private send(msg: ClientMsg): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(msg));
  }

  sit(seat: number): void { this.send({ t: 'sit', seat }); }
  setReady(ready: boolean): void { this.send({ t: 'ready', ready }); }
  start(): void { this.send({ t: 'start' }); }
  act(action: Action): void { this.send({ t: 'action', action }); }
  nextHand(): void { this.send({ t: 'nextHand' }); }

  openReview(): void {
    const v = this.state.view;
    // 门控：只有本手已结束（handComplete/gameOver）才允许打开复盘赢率
    if (!v || (v.phase !== 'handComplete' && v.phase !== 'gameOver')) return;
    this.patch({ reviewOpen: true, review: this.state.review ?? this.state.history[this.state.history.length - 1] ?? null });
  }

  /** 从「手牌历史」打开某一手复盘；同样受门控约束（对局进行中不可开）。 */
  openHistoryReview(handNumber: number): void {
    const v = this.state.view;
    if (!v || (v.phase !== 'handComplete' && v.phase !== 'gameOver')) return;
    const target = this.state.history.find((h) => h.handNumber === handNumber);
    if (!target) return;
    this.patch({ review: target, reviewOpen: true });
  }
  closeReview(): void { this.patch({ reviewOpen: false }); }

  leave(): void {
    this.manualClose = true;
    this.reconnectAttempts = 0;
    this.reconnectDeadline = 0;
    this.send({ t: 'leave' });
    this.cleanupSocket();
    clearSession(); // 主动离开：清理持久化，避免误重连
    this.store.set(() => ({ ...INITIAL }));
  }

  private cleanupSocket(): void {
    this.stopHeartbeat();
    this.awaitingFirstSync = false;
    this.clearFirstSyncTimer();
    if (this.reconnectTimer) { clearTimeout(this.reconnectTimer); this.reconnectTimer = null; }
    if (this.connectTimer) { clearTimeout(this.connectTimer); this.connectTimer = null; }
    if (this.ws) {
      const ws = this.ws;
      this.ws = null; // 先摘引用：close 回调据此判定为「旧连接」并静默忽略
      try { ws.onclose = null; ws.close(); } catch { /* noop */ }
    }
  }

  reset(): void {
    this.manualClose = true;
    this.reconnectAttempts = 0;
    this.reconnectDeadline = 0;
    this.cleanupSocket();
    clearSession();
    this.store.set(() => ({ ...INITIAL }));
  }
}

export const mpClient = new MpClient();
